import { Row, Col, Form, Input, InputNumber } from 'antd'
import formatIntegerBySpace from '../../../../helpers/formatters/format-integer-by-space'
import { DataSourceItem } from '../../shipments-page'
import { FC, useCallback, useContext, useState } from 'react'
import ShipmentPosition from '../../../../types/api/shipment-position'
import { ShipmentsPageCtx } from '../../shipments-page'
import { hasShipmentMaterialConsumptions } from '../../../../helpers/entities/shipment/get-shipment-material-consumptions'
import { isShipmentCalculatedOnActual } from '../../../../helpers/entities/shipment/get-shipment-display-calculated'

export const ExpandedRow: FC<{ record: DataSourceItem }> = ({ record }) => {
    const { onUpdateShipmentPosition } = useContext(ShipmentsPageCtx)
    const [copiesDraft, setCopiesDraft] = useState<Record<number, number>>({})
    const [actualCopiesDraft, setActualCopiesDraft] = useState<Record<number, number>>({})

    const showActualCopies =
        hasShipmentMaterialConsumptions(record) || isShipmentCalculatedOnActual(record)

    const updateShipmentPosition = useCallback(
        (index: number, field: keyof ShipmentPosition, value: string | number) => {
            const currentPositions = record.shipment_positions ?? []
            const currentPosition = currentPositions[index]

            if (!currentPosition || currentPosition[field] === value) {
                return
            }

            const data: Pick<ShipmentPosition, 'copies' | 'labelName' | 'comment'> &
                Partial<Pick<ShipmentPosition, 'actualCopies'>> = {
                copies: currentPosition.copies,
                labelName: currentPosition.labelName,
                comment: currentPosition.comment,
                [field]: value,
            }

            // Не шлём actualCopies: null — иначе можно затереть значение, выставленное при фиксации расхода
            if (field === 'actualCopies') {
                data.actualCopies = value as number
            } else if (currentPosition.actualCopies != null) {
                data.actualCopies = currentPosition.actualCopies
            }

            onUpdateShipmentPosition(record, currentPosition.documentId, data)
        },
        [onUpdateShipmentPosition, record]
    )

    return (
        <Row gutter={24}>
            <Col span={12}>
                <Row gutter={16}>
                    <Col sm={showActualCopies ? 5 : 7}>Наименование</Col>
                    <Col sm={3}>Тираж</Col>
                    {showActualCopies && <Col sm={3}>Фактический тираж</Col>}
                    <Col sm={showActualCopies ? 6 : 8}>Комментарий</Col>
                </Row>
                {record.shipment_positions?.map((pos, i) => {
                    const actualCopiesValue = actualCopiesDraft[i] ?? pos.actualCopies ?? pos.copies

                    return (
                        <Row gutter={16} key={pos.id}>
                            <Col sm={showActualCopies ? 5 : 7}>
                                <Form.Item noStyle>
                                    <Input
                                        defaultValue={pos.labelName}
                                        onBlur={e =>
                                            updateShipmentPosition(
                                                i,
                                                'labelName',
                                                e.target.value || ''
                                            )
                                        }
                                    />
                                </Form.Item>
                            </Col>
                            <Col sm={3}>
                                <Form.Item noStyle>
                                    <InputNumber
                                        controls={false}
                                        style={{ width: 96 }}
                                        value={copiesDraft[i] ?? pos.copies}
                                        formatter={formatIntegerBySpace}
                                        disabled={showActualCopies}
                                        onChange={value => {
                                            if (showActualCopies) return
                                            if (typeof value === 'number') {
                                                setCopiesDraft(prev => ({ ...prev, [i]: value }))
                                            }
                                        }}
                                        onBlur={() => {
                                            if (showActualCopies) return
                                            const nextValue = copiesDraft[i] ?? pos.copies
                                            updateShipmentPosition(i, 'copies', nextValue)
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            {showActualCopies && (
                                <Col sm={3}>
                                    <Form.Item noStyle>
                                        <InputNumber
                                            controls={false}
                                            style={{ width: 96 }}
                                            value={actualCopiesValue}
                                            formatter={formatIntegerBySpace}
                                            onChange={value => {
                                                if (typeof value === 'number') {
                                                    setActualCopiesDraft(prev => ({
                                                        ...prev,
                                                        [i]: value,
                                                    }))
                                                }
                                            }}
                                            onBlur={() => {
                                                const nextValue =
                                                    actualCopiesDraft[i] ??
                                                    pos.actualCopies ??
                                                    pos.copies
                                                updateShipmentPosition(i, 'actualCopies', nextValue)
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                            <Col sm={showActualCopies ? 6 : 8}>
                                <Form.Item noStyle>
                                    <Input
                                        defaultValue={pos.comment}
                                        onBlur={e =>
                                            updateShipmentPosition(
                                                i,
                                                'comment',
                                                e.target.value || ''
                                            )
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    )
                })}
            </Col>
        </Row>
    )
}
